import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Patient, PatientDetailResponse, ProgressResponse } from '@yui-receipt/shared'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import VasChart from '@/components/voice/VasChart'

export default function PatientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const { data, isLoading, error } = useQuery<PatientDetailResponse>({
    queryKey: ['patient', id],
    queryFn: () => api(`/patients/${id}`),
    enabled: !!id,
  })

  const { data: progress } = useQuery<ProgressResponse>({
    queryKey: ['patient-progress', id, 'vas_pain'],
    queryFn: () => api(`/patients/${id}/progress?metric=vas_pain`),
    enabled: !!id,
  })

  if (isLoading) {
    return <p className="max-w-3xl mx-auto p-6 text-slate-500">กำลังโหลด...</p>
  }
  if (error || !data) {
    return (
      <p className="max-w-3xl mx-auto p-6 text-rose-600">
        โหลดไม่สำเร็จ —{' '}
        <Link to="/patients" className="underline">
          กลับ
        </Link>
      </p>
    )
  }

  const p = data.patient
  const visits = data.visits

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{p.nameTh}</h1>
          <p className="text-xs text-slate-500 font-mono">
            HN {p.hn}
            {p.phone ? ` • ${p.phone}` : ''}
            {p.dob ? ` • เกิด ${p.dob}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/patients" className="text-sm text-slate-500 hover:text-brand-600">
            ← รายการ
          </Link>
          <Link
            to={`/visits/new?patientId=${p.id}`}
            className="btn-primary min-h-tap text-sm"
          >
            + เข้ารับการรักษาใหม่
          </Link>
        </div>
      </header>

      {editing ? (
        <PatientEditForm
          patient={p}
          onSaved={() => {
            setEditing(false)
            qc.invalidateQueries({ queryKey: ['patient', id] })
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <section className="card space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">ข้อมูลผู้ป่วย</h2>
            <button
              className="btn-secondary text-sm"
              onClick={() => setEditing(true)}
            >
              แก้ไข
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-slate-500">HN</dt>
            <dd className="font-mono">{p.hn}</dd>
            <dt className="text-slate-500">เพศ</dt>
            <dd>{p.sex === 'M' ? 'ชาย' : p.sex === 'F' ? 'หญิง' : '—'}</dd>
            <dt className="text-slate-500">วันเกิด</dt>
            <dd>{p.dob ?? '—'}</dd>
            <dt className="text-slate-500">เบอร์โทร</dt>
            <dd>{p.phone ?? '—'}</dd>
            <dt className="text-slate-500">อีเมล</dt>
            <dd>{p.email ?? '—'}</dd>
            <dt className="text-slate-500">ส่วนสูง / น้ำหนัก</dt>
            <dd>
              {p.heightCm ?? '—'} ซม • {p.weightKg ?? '—'} กก
            </dd>
            <dt className="text-slate-500">ติดต่อฉุกเฉิน</dt>
            <dd>{p.emergencyContact ?? '—'}</dd>
            <dt className="text-slate-500">โรคประจำตัว</dt>
            <dd className="whitespace-pre-wrap">{p.pmh ?? '—'}</dd>
            <dt className="text-slate-500">แพ้ยา</dt>
            <dd>{p.allergies ?? '—'}</dd>
            <dt className="text-slate-500">อาชีพ</dt>
            <dd>{p.occupation ?? '—'}</dd>
            <dt className="text-slate-500">ที่อยู่</dt>
            <dd className="whitespace-pre-wrap">{p.address ?? '—'}</dd>
            <dt className="text-slate-500">ยินยอม</dt>
            <dd>
              {p.consentAudio ? '🎙 audio' : '🎙 ✗'} •{' '}
              {p.consentAiScribe ? '🤖 AI' : '🤖 ✗'}
            </dd>
          </dl>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="font-semibold px-1">แนวโน้ม VAS Pain</h2>
        <VasChart
          points={progress?.data ?? []}
          yMax={10}
          unit={progress?.unit ?? 'คะแนน (0-10)'}
        />
      </section>

      <section className="card">
        <h2 className="font-semibold mb-2">ประวัติการรักษา</h2>
        {visits.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">ยังไม่มีประวัติการรักษา</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visits.map((v) => (
              <li key={v.id}>
                <Link
                  to={`/visits/${v.id}/record`}
                  className="flex items-center justify-between py-2 px-2 hover:bg-slate-50 rounded-md"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {v.visitDate} —{' '}
                      {v.visitType === 'initial' ? 'ครั้งแรก' : 'ติดตามผล'}
                    </div>
                    {v.timeIn && (
                      <div className="text-xs text-slate-500">เข้า {v.timeIn}</div>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">ดู →</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function PatientEditForm({
  patient,
  onSaved,
  onCancel,
}: {
  patient: Patient
  onSaved: () => void
  onCancel: () => void
}) {
  const [nameTh, setNameTh] = useState(patient.nameTh)
  const [nameEn, setNameEn] = useState(patient.nameEn ?? '')
  const [hn, setHn] = useState(patient.hn)
  const [dob, setDob] = useState(patient.dob ?? '')
  const [sex, setSex] = useState<'M' | 'F' | ''>(patient.sex ?? '')
  const [phone, setPhone] = useState(patient.phone ?? '')
  const [email, setEmail] = useState(patient.email ?? '')
  const [emergencyContact, setEmergencyContact] = useState(patient.emergencyContact ?? '')
  const [heightCm, setHeightCm] = useState(patient.heightCm?.toString() ?? '')
  const [weightKg, setWeightKg] = useState(patient.weightKg?.toString() ?? '')
  const [pmh, setPmh] = useState(patient.pmh ?? '')
  const [allergies, setAllergies] = useState(patient.allergies ?? '')
  const [occupation, setOccupation] = useState(patient.occupation ?? '')
  const [address, setAddress] = useState(patient.address ?? '')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!nameTh.trim()) {
      toastError('กรุณากรอกชื่อ')
      return
    }
    if (!hn.trim()) {
      toastError('กรุณากรอก HN')
      return
    }
    setSaving(true)
    try {
      await api(`/patients/${patient.id}`, {
        method: 'PATCH',
        body: {
          nameTh: nameTh.trim(),
          nameEn: nameEn.trim() || null,
          hn: hn.trim(),
          dob: dob || null,
          sex: sex || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          emergencyContact: emergencyContact.trim() || null,
          heightCm: heightCm ? Number(heightCm) : null,
          weightKg: weightKg ? Number(weightKg) : null,
          pmh: pmh.trim() || null,
          allergies: allergies.trim() || null,
          occupation: occupation.trim() || null,
          address: address.trim() || null,
        },
      })
      toastSuccess('บันทึกข้อมูลผู้ป่วยสำเร็จ')
      onSaved()
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      toastError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card space-y-4">
      <h2 className="font-semibold">แก้ไขข้อมูลผู้ป่วย</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">ชื่อ-นามสกุล (ไทย) *</label>
          <input className="input" value={nameTh} onChange={(e) => setNameTh(e.target.value)} />
        </div>
        <div>
          <label className="label">ชื่อ-นามสกุล (อังกฤษ)</label>
          <input className="input" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
        </div>
        <div>
          <label className="label">HN *</label>
          <input className="input font-mono" value={hn} onChange={(e) => setHn(e.target.value)} />
        </div>
        <div>
          <label className="label">วันเกิด</label>
          <input className="input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
        <div>
          <label className="label">เพศ</label>
          <select className="input" value={sex} onChange={(e) => setSex(e.target.value as 'M' | 'F' | '')}>
            <option value="">ไม่ระบุ</option>
            <option value="M">ชาย</option>
            <option value="F">หญิง</option>
          </select>
        </div>
        <div>
          <label className="label">เบอร์โทร</label>
          <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08x-xxx-xxxx" />
        </div>
        <div>
          <label className="label">อีเมล</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@email.com" />
        </div>
        <div>
          <label className="label">ติดต่อฉุกเฉิน</label>
          <input className="input" value={emergencyContact} onChange={(e) => setEmergencyContact(e.target.value)} />
        </div>
        <div>
          <label className="label">ส่วนสูง (ซม)</label>
          <input className="input" type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
        </div>
        <div>
          <label className="label">น้ำหนัก (กก)</label>
          <input className="input" type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="label">โรคประจำตัว</label>
        <textarea className="input min-h-[60px]" value={pmh} onChange={(e) => setPmh(e.target.value)} />
      </div>
      <div>
        <label className="label">แพ้ยา</label>
        <textarea className="input min-h-[60px]" value={allergies} onChange={(e) => setAllergies(e.target.value)} />
      </div>
      <div>
        <label className="label">อาชีพ</label>
        <input className="input" value={occupation} onChange={(e) => setOccupation(e.target.value)} />
      </div>
      <div>
        <label className="label">ที่อยู่</label>
        <textarea className="input min-h-[60px]" value={address} onChange={(e) => setAddress(e.target.value)} />
      </div>

      <div className="flex gap-2">
        <button className="btn-primary flex-1" onClick={handleSave} disabled={saving}>
          {saving ? 'กำลังบันทึก...' : 'บันทึก'}
        </button>
        <button className="btn-secondary flex-1" onClick={onCancel} disabled={saving}>
          ยกเลิก
        </button>
      </div>
    </section>
  )
}
